import { useState } from 'react'
import type {
  BookingSearchData,
  TravelerDetails,
  ArrivalInfo,
} from '../../pages/Booking'
import type { Vehicle } from '../../types/booking'
import BookingItineraryCard, {
  type BookingStop,
} from './BookingItineraryCard'
import { moreOptions } from './book-step3'
import { bookingService } from '../../api/services/bookingService'
import { loadStripe } from '@stripe/stripe-js'
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js'

const stripePromise = loadStripe(
  'pk_test_TYooMQauvdEDq54NiTphI7jx',
  {
    developerTools: {
      assistant: {
        enabled: false,
      },
    },
  } as any
)

type PaymentMethod =
  | 'cash'
  | 'online'
  | 'apple-pay'
  | 'google-pay'
  | 'card'

type BookStep4Props = {
  selectedVehicle: Vehicle | null
  onBack: () => void
  onComplete?: (
    paymentMethod: PaymentMethod,
    bookingId?: string
  ) => void
  searchData: BookingSearchData
  serviceType: string
  serviceId: string
  servicePrice?: number
  serviceTitle?: string
  stops: BookingStop[]
  onEditRoute?: () => void
  onSearchDataChange?: (
    updater: (
      prev: BookingSearchData
    ) => BookingSearchData
  ) => void
  travelerDetails: TravelerDetails
  arrivalInfo: ArrivalInfo
  specialRequirements: string
  selectedOptions: string[]
}

type PaymentStrategy =
  | 'full'
  | 'advanced'
  | 'cash'

function BookStep4Inner({
  selectedVehicle,
  onBack,
  onComplete,
  searchData,
  serviceType,
  serviceId,
  stops,
  onEditRoute,
  onSearchDataChange,
  travelerDetails,
  arrivalInfo,
  specialRequirements,
  selectedOptions,
  servicePrice,
  serviceTitle,
  strategy,
  setStrategy,
  totalPrice,
  depositAmount,
  optionsPrice,
  appliedPromo,
  setAppliedPromo,
  transportPrice,
}: BookStep4Props & {
  strategy: PaymentStrategy
  setStrategy: (
    val: PaymentStrategy
  ) => void
  totalPrice: number
  depositAmount: number
  optionsPrice: number
  appliedPromo: string
  setAppliedPromo: (val: string) => void
  transportPrice: number
}) {
  const stripe = useStripe()
  const elements = useElements()

  const [promoOpen, setPromoOpen] =
    useState(false)

  const [promoCode, setPromoCode] =
    useState('')

  const [promoMessage, setPromoMessage] =
    useState('')

  const [isProcessing, setIsProcessing] =
    useState(false)

  const discount =
    appliedPromo ? 20 : 0

  const handlePromoCode = () => {
    const trimmedCode =
      promoCode.trim()

    if (!trimmedCode) {
      setPromoMessage(
        'Enter a promo code'
      )
      return
    }

    setAppliedPromo(trimmedCode)
    setPromoMessage(
      'Promo code applied'
    )
  }

  const handleSubmit = async (
    e: React.MouseEvent
  ) => {
    e.preventDefault()

    const positionStr =
      searchData.position
        ? typeof searchData.position ===
          'string'
          ? searchData.position
          : `${searchData.position.lat},${searchData.position.lng}`
        : null

    const keywordStr =
      searchData.keyword ||
      searchData.fromTitle ||
      searchData.fromAddress ||
      ''

    const hasArrivalDetails =
      Boolean(
        arrivalInfo?.number &&
          arrivalInfo.number.trim()
      )

    const location =
      hasArrivalDetails
        ? JSON.stringify({
            type: arrivalInfo.type,
            company:
              arrivalInfo.airlineOrOperator,
            flightOrTrainNumber:
              arrivalInfo.number,
            arrivalLocation:
              arrivalInfo.terminal,
            arrivalTime:
              arrivalInfo.time,
          })
        : JSON.stringify({
            type: 'google_maps',
            selectedLocation:
              keywordStr ||
              searchData.fromAddress ||
              'Salzburg, Austria',
            position: positionStr,
          })

    const submitPayload = {
      service_type: serviceType,
      service_id: Number(serviceId),

      /*
       * Hourly bookings do not use a separate
       * service-level price. Their transport
       * price is calculated as:
       *
       * hourlyPrice × durationHours
       *
       * The final total is handled below through
       * transportPrice.
       */
      service_price:
        serviceType === 'hourly'
          ? 0
          : servicePrice ?? 0,

      // Location, Position & Keyword
      location,
      position: positionStr,
      keyword: keywordStr,

      pickup_latitude:
        typeof searchData.position ===
        'object'
          ? searchData.position?.lat
          : null,

      pickup_longitude:
        typeof searchData.position ===
        'object'
          ? searchData.position?.lng
          : null,

      pickup_location:
        searchData.fromAddress
          ? searchData.fromTitle &&
            !searchData.fromAddress
              .toLowerCase()
              .includes(
                searchData.fromTitle.toLowerCase()
              )
            ? `${searchData.fromTitle}, ${searchData.fromAddress}`
            : searchData.fromAddress
          : searchData.fromTitle ||
            'Salzburg, Austria',

      drop_location:
        searchData.toAddress,

      pickup_datetime:
        `${searchData.date}T${searchData.time}:00`,

      return_datetime:
        searchData.returnDate &&
        searchData.returnTime
          ? `${searchData.returnDate}T${searchData.returnTime}:00`
          : null,

      return_trip: Boolean(
        searchData.returnDate &&
          searchData.returnTime
      ),

      passengers:
        Number(
          searchData.adults || 0
        ) +
        Number(
          searchData.children || 0
        ),

      luggage: Number(
        searchData.extraBags || 0
      ),

      // Keep the selected vehicle ID
      // for booking validation.
      vehicle:
        selectedVehicle
          ? Number(
              selectedVehicle.id
            )
          : null,

      /*
       * Vehicle price sent to the backend.
       *
       * Day Trip:
       *   No fleet vehicle charge.
       *
       * Hourly:
       *   hourlyPrice × durationHours.
       *
       * Normal transfer:
       *   Already calculated in Booking.tsx.
       */
      // vehicle_price:
      //   serviceType === 'daytrips'
      //     ? 0
      //     : transportPrice,

      vehicle_price: transportPrice,

      traveler_name:
        travelerDetails.name,

      traveler_phone:
        travelerDetails.phone,

      traveler_email:
        travelerDetails.email,

      booking_for_someone_else:
        travelerDetails.bookingForSomeoneElse,

      other_person_name:
        travelerDetails.otherPersonName,

      other_person_phone:
        travelerDetails.otherPersonPhone,

      selected_options:
        selectedOptions,

      payment_method: strategy,

      deposit_amount:
        depositAmount,

      promotion_code:
        appliedPromo || null,

      estimated_price:
        totalPrice,

      final_price:
        totalPrice,

      notes:
        specialRequirements,
    }

    if (location.length > 255) {
      alert(
        'The selected location details are too long. Please use a shorter location.'
      )
      return
    }

    if (strategy === 'cash') {
      setIsProcessing(true)

      const res =
        await bookingService.submitBooking(
          submitPayload
        )

      setIsProcessing(false)

      if (res.success) {
        onComplete?.(
          'cash',
          res.bookingId
        )
      } else {
        alert(res.message)
      }

      return
    }

    if (!stripe || !elements) {
      return
    }

    setIsProcessing(true)

    // In a real app, this confirms the PaymentIntent.
    // Since we're in 'payment' deferred mode for UI mock,
    // we use elements.submit().
    const { error } =
      await elements.submit()

    if (error) {
      console.log(
        'Stripe error',
        error
      )

      setIsProcessing(false)
    } else {
      const res =
        await bookingService.submitBooking(
          submitPayload
        )

      setIsProcessing(false)

      if (res.success) {
        onComplete?.(
          'online',
          res.bookingId
        )
      } else {
        alert(res.message)
      }
    }
  }

  const strategies = [
    {
      id: 'full' as PaymentStrategy,
      label: 'Pay in Full Online',
      desc: `€${totalPrice.toFixed(2)} today`,
      icon: '💳',
    },
    {
      id: 'advanced' as PaymentStrategy,
      label: 'Pay 20% Deposit Online',
      desc: `€${depositAmount.toFixed(
        2
      )} today, €${(
        totalPrice - depositAmount
      ).toFixed(2)} to driver`,
      icon: '⏱️',
    },
    {
      id: 'cash' as PaymentStrategy,
      label: 'Pay Cash to Driver',
      desc: `€${totalPrice.toFixed(
        2
      )} at pickup`,
      icon: '💵',
    },
  ]

  return (
    <section className="relative px-4 pb-[140px] sm:px-8 sm:pb-20 lg:px-[5.5vw]">
      <div className="mx-auto w-full max-w-[1450px]">
        <div className="mt-8">
          <h1 className="font-host text-[22px] font-semibold tracking-[-0.03em] text-black sm:text-[30px]">
            Payment
          </h1>

          <p className="mt-2 font-poppins text-[14px] leading-[1.55] text-[#333333] sm:text-[16px]">
            Choose your preferred payment method and complete your booking securely.
          </p>
        </div>

        <div className="mt-5 grid items-start gap-8 lg:grid-cols-[minmax(0,1.34fr)_minmax(410px,0.86fr)] lg:gap-10">
          <div>
            <div className="overflow-hidden rounded-[24px] bg-white shadow-[0_5px_22px_rgba(0,0,0,0.08)]">
              <div className="flex min-h-[72px] flex-wrap items-center justify-between gap-3 border-b border-[#E2E2E2] px-4 py-4 sm:min-h-[82px] sm:px-7 sm:py-5">
                <h2 className="font-host text-[16px] font-medium text-[#444444] sm:text-[18px]">
                  Payment method
                </h2>

                {/* <button
                  type="button"
                  onClick={() =>
                    setPromoOpen(
                      (current) =>
                        !current
                    )
                  }
                  className="inline-flex items-center gap-2 rounded-full bg-[#EEF1FF] px-3 py-[9px] font-poppins text-[11px] font-medium text-[#4169E1] sm:px-4 sm:py-[10px] sm:text-[12px]"
                >
                  <span className="flex h-[23px] w-[23px] items-center justify-center rounded-[7px] bg-[#5E77F4] text-[12px] font-semibold text-white">
                    %
                  </span>

                  <span className="sm:hidden">
                    Promo
                  </span>

                  <span className="hidden sm:inline">
                    Add promo code
                  </span>
                </button> */}
              </div>

              {/* {promoOpen && (
                <div className="border-b border-[#E5E5E5] bg-[#FAFAFA] px-6 py-4 sm:px-7">
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <input
                      type="text"
                      value={promoCode}
                      onChange={(
                        event
                      ) => {
                        setPromoCode(
                          event.target.value
                        )
                        setPromoMessage('')
                      }}
                      placeholder="Enter promo code"
                      className="h-[46px] flex-1 rounded-[10px] border border-[#D5D5D5] bg-white px-4 font-poppins text-[12px] text-black outline-none focus:border-[#9BC344]"
                    />

                    <button
                      type="button"
                      onClick={
                        handlePromoCode
                      }
                      className="h-[46px] rounded-[10px] bg-[#365E92] px-7 font-poppins text-[11px] font-medium text-white"
                    >
                      Apply
                    </button>
                  </div>

                  {promoMessage && (
                    <p
                      className={`mt-2 font-poppins text-[10px] ${
                        appliedPromo
                          ? 'text-[#65951E]'
                          : 'text-[#D84B4B]'
                      }`}
                    >
                      {promoMessage}
                    </p>
                  )}
                </div>
              )} */}

              <div className="space-y-3 p-4 sm:p-7">
                {strategies.map(
                  (strat) => {
                    const isSelected =
                      strategy ===
                      strat.id

                    return (
                      <button
                        key={strat.id}
                        type="button"
                        onClick={() =>
                          setStrategy(
                            strat.id
                          )
                        }
                        className={`flex min-h-[72px] w-full items-center justify-between gap-3 rounded-[13px] border px-3 py-3 text-left transition-all duration-200 sm:min-h-[80px] sm:gap-4 sm:px-6 sm:py-4 lg:min-h-[64px] lg:px-4 lg:py-2.5 ${
                          isSelected
                            ? 'border-[2px] border-[#A9CF55] bg-[#F2FFD8]/20'
                            : 'border-[#D9D9D9] bg-white hover:border-[#B7CD7C]'
                        }`}
                      >
                        <span className="flex min-w-0 items-center gap-3 sm:gap-4">
                          <span
                            className={`flex h-[29px] w-[29px] shrink-0 items-center justify-center rounded-full border ${
                              isSelected
                                ? 'border-[#CFCFCF]'
                                : 'border-[#D0D0D0]'
                            }`}
                          >
                            {isSelected && (
                              <span className="h-[12px] w-[12px] rounded-full bg-[#A9CF55]" />
                            )}
                          </span>

                          <span className="min-w-0 flex flex-col">
                            <span className="font-host text-[13px] font-semibold text-black sm:text-[15px] lg:text-[14px]">
                              {strat.label}
                            </span>

                            <span className="font-poppins text-[11px] text-[#555555]">
                              {strat.desc}
                            </span>
                          </span>
                        </span>

                        <span className="text-[24px] opacity-80">
                          {strat.icon}
                        </span>
                      </button>
                    )
                  }
                )}

                {strategy === 'cash' ? (
                    <div className="mt-6 animate-in border-t border-[#E5E5E5] pt-6 fade-in slide-in-from-top-4 duration-300">
                     <div className="rounded-[12px] border border-[#D7DEE8] bg-[#F7F9FC] px-4 py-4 sm:px-5 sm:py-5">
                        <div className="flex items-start gap-3">
                          <span className="flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-full bg-[#E8EEF7] text-[14px] font-semibold text-[#355C8A]">
                            i
                          </span>

                          <p className="font-poppins text-[11px] leading-[1.7] text-[#4B5563] sm:text-[12px]">
                            Cash payment bookings are waitlisted, but your vehicle is held.
                            We will contact you before your travel date to reconfirm.
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-6 animate-in border-t border-[#E5E5E5] pt-6 fade-in slide-in-from-top-4 duration-300">
                      <h3 className="mb-4 font-host text-[16px] font-medium text-[#444444]">
                        Enter Payment Details
                      </h3>

                      <div className="min-h-[150px] rounded-[10px]">
                        <PaymentElement
                          options={{
                            layout: 'tabs',
                          }}
                        />
                      </div>
                    </div>
                  )}
              </div>
            </div>

            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row sm:gap-6">
              <div className="flex items-center gap-3">
                <img
                  src="/assets/images/ssl.png"
                  alt=""
                  className="h-[26px] w-[26px] object-contain"
                />

                <span className="font-poppins text-[11px] font-medium text-[#16A663] sm:text-[12px]">
                  SSL protected
                </span>
              </div>

              <span className="hidden h-[42px] w-px bg-[#AFAFAF] sm:block" />

              <p className="max-w-[280px] text-center font-poppins text-[10px] leading-[1.6] text-[#555555] sm:max-w-[240px] sm:text-left sm:text-[11px]">
                By booking, you agree to Salzburg Cab
                <br />
                Terms of use and Privacy policy
              </p>
            </div>
          </div>

          <BookingItineraryCard
            searchData={searchData}
            selectedVehicle={
              selectedVehicle
            }
            stops={stops}
            extraPrice={
              optionsPrice
            }
            extraPriceLabel="Additional Options"
            discount={discount}
            onEditRoute={
              onEditRoute
            }
            onSearchDataChange={
              onSearchDataChange
            }
            arrivalInfo={
              arrivalInfo
            }
            specialRequirements={
              specialRequirements
            }
            servicePrice={
              servicePrice
            }
            serviceTitle={
              serviceTitle
            }
            serviceType={
              serviceType
            }
          />
        </div>
      </div>

      <div className="fixed bottom-3 left-1/2 z-[100] w-[calc(100%-1.5rem)] -translate-x-1/2 rounded-[18px] border border-[#ECECEC] bg-white/95 px-3 py-3 shadow-[0_5px_22px_rgba(0,0,0,0.14)] backdrop-blur-md sm:bottom-4 sm:w-[88vw] sm:px-10 sm:py-5 lg:w-[68vw] lg:max-w-[1120px]">
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          <button
            type="button"
            onClick={onBack}
            className="flex min-h-[48px] min-w-0 flex-1 items-center justify-center rounded-full bg-black px-4 font-poppins text-[12px] font-medium text-white sm:min-h-[54px] sm:min-w-[190px] sm:flex-none sm:px-7"
          >
            Back
          </button>

          <button
            type="button"
            disabled={
              isProcessing
            }
            onClick={
              handleSubmit
            }
            className="flex min-h-[48px] min-w-0 flex-1 items-center justify-center rounded-full bg-[#FF6B2C] px-4 font-poppins text-[12px] font-bold text-white transition-transform duration-200 hover:-translate-y-0.5 sm:min-h-[54px] sm:min-w-[190px] sm:flex-none sm:px-7 disabled:opacity-70 disabled:hover:translate-y-0"
          >
            {isProcessing
              ? 'Processing...'
              : 'Complete Booking'}
          </button>
        </div>
      </div>
    </section>
  )
}

export default function BookStep4(
  props: BookStep4Props
) {
  const [strategy, setStrategy] =
    useState<PaymentStrategy>('full')

  const [appliedPromo, setAppliedPromo] =
    useState('')

  /*
   * Calculate the actual vehicle
   * charge for this booking.
   *
   * Day Trip:
   *   No fleet vehicle charge here.
   *   The day-trip service price is
   *   handled separately.
   *
   * Hourly:
   *   hourlyPrice × durationHours
   *
   * Normal transfer:
   *   selectedVehicle.price already contains
   *   the distance-based calculation from
   *   Booking.tsx.
   */
  // const transportPrice =
  //   props.serviceType ===
  //   'daytrips'
  //     ? 0
  //     : props.serviceType ===
  //       'hourly'
  //       ? Number(
  //           props.selectedVehicle
  //             ?.hourlyPrice ?? 0
  //         ) *
  //         (
  //           Number(
  //             props.searchData
  //               .durationHours
  //           ) > 0
  //             ? Number(
  //                 props.searchData
  //                   .durationHours
  //               )
  //             : 1
  //         )
  //       : Number(
  //           props.selectedVehicle
  //             ?.price ?? 0
  //         )
  const transportPrice =
    props.serviceType === 'daytrips'
      ? props.selectedVehicle
        ? Math.round(
            Number(
              (
                props.selectedVehicle as Vehicle & {
                  dayTripPrice?: number
                }
              ).dayTripPrice ??
                props.selectedVehicle.price ??
                0
            )
          )
        : 0
      : props.serviceType === 'hourly'
        ? Number(
            props.selectedVehicle?.hourlyPrice ?? 0
          ) *
          (
            Number(props.searchData.durationHours) > 0
              ? Number(props.searchData.durationHours)
              : 1
          )
        : Number(props.selectedVehicle?.price ?? 0)
  /*
   * Hourly bookings are priced entirely
   * from the vehicle hourly rate.
   *
   * This prevents an old/stale servicePrice
   * from being added to the hourly total.
   */
  const servicePrice =
    props.serviceType === 'hourly'
      ? 0
      : props.servicePrice ?? 0

  
  const optionsPrice = (
    props.selectedOptions || []
  ).reduce(
    (acc, optId) => {
      const found = moreOptions.find(
        (o) => o.id === optId
      )

      return acc + (found?.price || 0)
    },
    0
  ) * (
    props.searchData.returnDate &&
    props.searchData.returnTime
      ? 2
      : 1
  )


  const discount =
    appliedPromo ? 20 : 0

  // const totalPrice = Math.max(
  //   transportPrice +
  //     servicePrice +
  //     optionsPrice -
  //     discount,
  //   0
  // )

  const calculatedTotalPrice =
    transportPrice +
    servicePrice +
    optionsPrice -
    discount

  const totalPrice = Math.max(
    props.serviceType === 'daytrips'
      ? Math.round(calculatedTotalPrice)
      : calculatedTotalPrice,
    0
  )
  const depositAmount =
    totalPrice > 0
      ? Math.max(
          Math.round(
            totalPrice * 0.2
          ),
          1
        )
      : 0

  const amountToCharge =
    strategy === 'advanced'
      ? depositAmount
      : totalPrice

  const stripeAmount =
    amountToCharge > 0
      ? amountToCharge * 100
      : 100

  const options = {
    mode: 'payment' as const,
    amount: stripeAmount,
    currency: 'eur',
    appearance: {
      theme: 'stripe' as const,
      variables: {
        colorPrimary:
          '#A9CF55',
        borderRadius: '10px',
      },
    },
  }

  return (
    <Elements
      stripe={stripePromise}
      options={options}
      key={amountToCharge}
    >
      <BookStep4Inner
        {...props}
        strategy={strategy}
        setStrategy={
          setStrategy
        }
        totalPrice={
          totalPrice
        }
        depositAmount={
          depositAmount
        }
        optionsPrice={
          optionsPrice
        }
        appliedPromo={
          appliedPromo
        }
        setAppliedPromo={
          setAppliedPromo
        }
        transportPrice={
          transportPrice
        }
      />
    </Elements>
  )
}
